import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ShieldCheck, Lock, Zap, X } from 'lucide-react';
const COUNTDOWN_SECONDS = 3 * 60 + 50; // 3:50
function LegalModal({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={onClose} data-id="element-415">
      <div className="relative w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl border border-matrix-border bg-matrix-card p-6 md:p-8" onClick={e => e.stopPropagation()} data-id="element-416">
        <button onClick={onClose} className="absolute top-4 right-4 text-matrix-text-dim hover:text-matrix-text transition-colors" data-id="element-417">
          <X className="h-5 w-5" data-id="element-418" />
        </button>
        <h2 className="font-body text-lg font-bold text-matrix-text mb-4" data-id="element-419">
          {title}
        </h2>
        <div className="font-body text-sm leading-relaxed text-matrix-text-muted space-y-3" data-id="element-420">
          {children}
        </div>
      </div>
    </div>;
}
export function VSLPage() {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [showButton, setShowButton] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);
  // Load Wistia scripts
  useEffect(() => {
    const loadScript = (src: string, type?: string) => {
      if (document.querySelector(`script[src="${src}"]`)) return;
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      if (type) script.type = type;
      document.head.appendChild(script);
    };
    loadScript('https://fast.wistia.com/player.js');
    loadScript('https://fast.wistia.com/embed/1rnyt8boov.js', 'module');
  }, []);
  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) {
      setShowButton(true);
      return;
    }
    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowButton(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  const handleBuyNow = useCallback(() => {
    window.open('https://www.voicesoffreedomus.com/cart/45290003792058:1', '_blank', 'noopener,noreferrer');
  }, []);
  return <div className="min-h-screen bg-matrix-bg flex flex-col" data-id="element-421">
      <div className="flex-1 flex flex-col items-center justify-start px-5 py-10 md:py-16" data-id="element-422">
        <div className="w-full max-w-2xl mx-auto" data-id="element-423">
          {/* Headline */}
          <div className="text-center mb-8" data-id="element-424">
            <h1 className="font-body text-2xl font-bold leading-tight text-matrix-text md:text-[2.25rem] md:leading-[1.2]" data-id="element-425">
              Recalibra tu Mente en 30 Días:{' '}
              <span className="text-matrix-gold" data-id="element-426">
                El Protocolo de 4 Semanas con Meditaciones Guiadas
                Transformadoras
              </span>
            </h1>
            <p className="mt-4 font-body text-base leading-relaxed text-matrix-text-muted md:text-lg" data-id="element-427">
              Cuatro Semanas, Cuatro Meditaciones Específicas Para Liberar
              Bloqueos, Reescribir Patrones Limitantes y Despertar una Nueva
              Realidad en 30 Días.
            </p>
          </div>

          {/* Video */}
          <div className="rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(201,168,76,0.08)] border border-matrix-border/30" data-id="element-428">
            <div ref={el => {
            if (el && !el.querySelector('wistia-player')) {
              const style = document.createElement('style');
              style.textContent = "wistia-player[media-id='1rnyt8boov']:not(:defined){background:center/contain no-repeat url('https://fast.wistia.com/embed/medias/1rnyt8boov/swatch');display:block;filter:blur(5px);padding-top:56.25%}";
              el.appendChild(style);
              const player = document.createElement('wistia-player');
              player.setAttribute('media-id', '1rnyt8boov');
              player.setAttribute('aspect', '1.7777777777777777');
              el.appendChild(player);
            }
          }} data-id="element-429" />
          </div>

          {/* Timer + CTA area */}
          <div className="mt-8 text-center min-h-[120px]" data-id="element-430">
            <AnimatePresence mode="wait" data-id="element-431">
              {!showButton /* Hidden timer state - keeps layout stable but invisible */ ? <div key="hidden-timer" className="opacity-0 pointer-events-none h-1" data-id="element-432"></div> : <motion.div key="cta" initial={{
              opacity: 0,
              scale: 0.95,
              y: 10
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }} className="flex flex-col items-center gap-4" data-id="element-433">
                  <p className="font-body text-sm font-medium text-matrix-gold" data-id="element-434">
                    🔓 ¡Oferta desbloqueada!
                  </p>

                  <button onClick={handleBuyNow} className="group w-full max-w-md inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-matrix-gold to-matrix-gold-bright px-8 py-5 font-body text-base font-bold uppercase tracking-wider text-matrix-bg shadow-[0_0_40px_rgba(201,168,76,0.25)] transition-all duration-300 hover:shadow-[0_0_60px_rgba(201,168,76,0.4)] hover:scale-[1.02] active:scale-[0.98]" data-id="element-435">
                    Quiero el Pack Completo — $37
                    <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-436" />
                  </button>

                  <div className="flex items-center justify-center gap-4 font-body text-xs text-matrix-text-dim" data-id="element-437">
                    <span className="inline-flex items-center gap-1" data-id="element-438">
                      <Lock className="h-3 w-3 text-matrix-gold-muted" data-id="element-439" />
                      Pago seguro
                    </span>
                    <span className="inline-flex items-center gap-1" data-id="element-440">
                      <ShieldCheck className="h-3 w-3 text-matrix-gold-muted" data-id="element-441" />
                      Garantía 7 días
                    </span>
                  </div>

                  <p className="font-body text-xs text-matrix-text-dim mt-1" data-id="element-442">
                    Acceso instantáneo &bull; eBook + Audiolibro + Meditaciones
                    Guiadas &bull; Bonus DMT incluido
                  </p>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-5 text-center border-t border-matrix-border/30" data-id="element-443">
        <p className="font-body text-[11px] text-matrix-text-muted/70 leading-relaxed max-w-lg mx-auto" data-id="element-444">
          This site is not a part of the Facebook website or Facebook Inc.
          Additionally, this site is NOT endorsed by Facebook in any way.
          FACEBOOK is a trademark of FACEBOOK, Inc.
        </p>
        <div className="mt-3 flex items-center justify-center gap-3" data-id="element-445">
          <button onClick={() => setShowPrivacy(true)} className="font-body text-[11px] text-matrix-text-muted/80 underline underline-offset-2 hover:text-matrix-text transition-colors" data-id="element-446">
            Privacy Policy
          </button>
          <span className="text-matrix-text-muted/30" data-id="element-447">|</span>
          <button onClick={() => setShowTerms(true)} className="font-body text-[11px] text-matrix-text-muted/80 underline underline-offset-2 hover:text-matrix-text transition-colors" data-id="element-448">
            Terms of Service
          </button>
        </div>
        <p className="mt-2 font-body text-[10px] text-matrix-text-muted/50" data-id="element-449">
          © {new Date().getFullYear()} Voices of Freedom. All rights reserved.
        </p>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacy && <LegalModal title="Privacy Policy" onClose={() => setShowPrivacy(false)} data-id="element-450">
          <p data-id="element-451">
            <strong data-id="element-452">Effective Date:</strong> February 2025
          </p>
          <p data-id="element-453">
            Your privacy is important to us. This Privacy Policy explains how we
            collect, use, disclose, and safeguard your information when you
            visit our website and use our services.
          </p>
          <p data-id="element-454">
            <strong data-id="element-455">Information We Collect:</strong> We may collect personal
            information that you voluntarily provide to us when you purchase
            products, subscribe to our newsletter, or contact us. This includes
            your name, email address, and payment information.
          </p>
          <p data-id="element-456">
            <strong data-id="element-457">How We Use Your Information:</strong> We use the information
            we collect to process transactions, send periodic emails regarding
            your order or other products and services, and improve our website
            and customer service.
          </p>
          <p data-id="element-458">
            <strong data-id="element-459">Third-Party Disclosure:</strong> We do not sell, trade, or
            otherwise transfer your personally identifiable information to
            outside parties. This does not include trusted third parties who
            assist us in operating our website, conducting our business, or
            servicing you, so long as those parties agree to keep this
            information confidential.
          </p>
          <p data-id="element-460">
            <strong data-id="element-461">Cookies:</strong> We use cookies to understand and save your
            preferences for future visits and compile aggregate data about site
            traffic and site interaction.
          </p>
          <p data-id="element-462">
            <strong data-id="element-463">Facebook Pixel & Analytics:</strong> We use Facebook Pixel
            and similar tracking technologies to measure the effectiveness of
            our advertising, understand user behavior, and deliver relevant ads.
            Data collected through these tools is subject to Facebook's Data
            Policy.
          </p>
          <p data-id="element-464">
            <strong data-id="element-465">Your Rights:</strong> You may opt out of receiving future
            emails at any time by contacting us. You can also request access to,
            correction of, or deletion of your personal data.
          </p>
          <p data-id="element-466">
            <strong data-id="element-467">Contact:</strong> If you have any questions regarding this
            privacy policy, you may contact us at soporte@voicesoffreedomus.com.
          </p>
        </LegalModal>}

      {/* Terms of Service Modal */}
      {showTerms && <LegalModal title="Terms of Service" onClose={() => setShowTerms(false)} data-id="element-468">
          <p data-id="element-469">
            <strong data-id="element-470">Effective Date:</strong> February 2025
          </p>
          <p data-id="element-471">
            By accessing and using this website, you accept and agree to be
            bound by the terms and provisions of this agreement.
          </p>
          <p data-id="element-472">
            <strong data-id="element-473">Products & Services:</strong> We offer digital products
            including eBooks, audiobooks, and guided meditations. All sales are
            final except as outlined in our refund policy. Digital products are
            delivered electronically upon purchase.
          </p>
          <p data-id="element-474">
            <strong data-id="element-475">Refund Policy:</strong> We offer a 7-day money-back
            guarantee. If you are not satisfied with your purchase, contact us
            within 7 days of purchase at soporte@voicesoffreedomus.com with the
            subject line "Solicitud de Reembolso" for a full refund.
          </p>
          <p data-id="element-476">
            <strong data-id="element-477">Intellectual Property:</strong> All content on this website,
            including text, graphics, logos, images, audio, and digital
            downloads, is the property of Voices of Freedom and is protected by
            international copyright laws. You may not reproduce, distribute, or
            create derivative works without express written permission.
          </p>
          <p data-id="element-478">
            <strong data-id="element-479">Disclaimer:</strong> The information provided on this
            website and in our products is for educational and informational
            purposes only. It is not intended as a substitute for professional
            medical advice, diagnosis, or treatment. Results may vary from
            person to person.
          </p>
          <p data-id="element-480">
            <strong data-id="element-481">Limitation of Liability:</strong> In no event shall Voices
            of Freedom be liable for any indirect, incidental, special,
            consequential, or punitive damages arising out of your use of our
            website or products.
          </p>
          <p data-id="element-482">
            <strong data-id="element-483">Governing Law:</strong> These terms shall be governed by and
            construed in accordance with applicable laws, without regard to
            conflict of law principles.
          </p>
          <p data-id="element-484">
            <strong data-id="element-485">Contact:</strong> For questions about these terms, contact
            us at soporte@voicesoffreedomus.com.
          </p>
        </LegalModal>}
    </div>;
}